import {
  Box,
  HStack,
  IconButton,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import {
  CreatePromoCodeRequest,
  EventDetailPromocodesSection,
  UpdatePromoCodeRequest,
} from "@common/interfaces/KYDEvent";
import { FaPlus } from "react-icons/fa";
import { PromoCodesTable } from "./PromoCodesTable";
import { KYDPromoCode } from "@common/interfaces/KYDCart";
import { SectionProps } from "../sections/sectionsComponents";
import { EditModalType } from "../../../utils";
import { useCallback, useState } from "react";
import EditPromocodeModal, { PromocodesValue } from "./EditPromocodeModal";
import { useLoading } from "../../hooks/form";
import actions from "../../../actions";
import { useDialog } from "../../../Common/Dialog";
import ShowIfHasPermission from "../../ShowIfHasPermission";
import EventDetailTitle from "../../EventDetailTitle";

const PromocodesSection = ({
  data = {
    promo_codes: [],
    ticket_types: [],
    tiered_ticket_types: [],
    currency: undefined,
    id: undefined,
    link: undefined,
    short_link: undefined,
    status: undefined,
    timezone: undefined,
  },
  permissions,
  isLoading,
  refetchEvent,
  setModalType,
  modalType,
}: SectionProps<EventDetailPromocodesSection["data"]>) => {
  const [editingPromoCode, setEditingPromoCode] = useState<KYDPromoCode | null>(
    null
  );
  const [loading, updateLoading] = useLoading({
    promocode: false,
  });
  const toast = useToast();
  const dialog = useDialog();

  const onCreatePromoCode = useCallback(
    async (createData: CreatePromoCodeRequest) => {
      let _;
      updateLoading("promocode", true);
      try {
        if (!data.id) {
          throw Error("NO event ID provided");
        }

        const request: CreatePromoCodeRequest = {
          code: createData.code?.trim(),
          amount: createData.amount,
          ticket_type_ids: createData.ticket_type_ids,
          mode: createData.mode || "percent",
          enabled: createData.enabled,
          max_total_uses: createData.max_total_uses,
          max_uses_per_user: createData.max_uses_per_user,
        };

        _ = await actions.ADMIN.createPromoCode(data.id, request);
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);
        toast({
          title: "Promo Code Created",
          description: "Promo code has been created",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("promocode", false);
      return _;
    },
    [updateLoading, refetchEvent, dialog]
  );

  const onUpdatePromoCode = useCallback(
    async (promoCode: PromocodesValue) => {
      let _;
      updateLoading("promocode", true);
      try {
        const request: UpdatePromoCodeRequest = {
          code: promoCode.code?.trim(),
          amount: promoCode.amount.toString(),
          ticket_type_ids: promoCode.ticket_type_ids,
          mode: promoCode.mode,
          enabled: promoCode.enabled,
          max_total_uses: promoCode.max_total_uses
            ? promoCode.max_total_uses.toString()
            : undefined,
          max_uses_per_user: promoCode.max_uses_per_user
            ? promoCode.max_uses_per_user.toString()
            : undefined,
        };

        _ = await actions.ADMIN.updatePromoCode(
          data.id,
          editingPromoCode.id,
          request
        );
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);
        toast({
          title: "Promo Code Updated",
          description: "Promo code has been updated",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("promocode", false);
      return _;
    },
    [updateLoading, editingPromoCode, refetchEvent, dialog]
  );

  const hasPermission = (permissionToCheck) =>
    permissions.includes(permissionToCheck);

  if (!data) {
    return null;
  }
  return (
    <Stack>
      <EditPromocodeModal
        currency={data.currency}
        isOpen={modalType === EditModalType.PROMO_CODE}
        data={data}
        isLoading={isLoading || loading.promocode}
        onClose={() => {
          setEditingPromoCode(null);
          setModalType(EditModalType.INACTIVE);
        }}
        onSave={editingPromoCode ? onUpdatePromoCode : onCreatePromoCode}
        promoCode={editingPromoCode}
      />

      <HStack justify={"space-between"} w="100%">
        <EventDetailTitle>Promo Codes</EventDetailTitle>
        <ShowIfHasPermission permissions={permissions} checkFor="write">
          <IconButton
            isDisabled={isLoading}
            variant={"outline"}
            aria-label="Add promo code"
            icon={<FaPlus />}
            onClick={() => setModalType(EditModalType.PROMO_CODE)}
          />
        </ShowIfHasPermission>
      </HStack>
      <Box overflowX={data.promo_codes.length === 0 ? "inherit" : "auto"}>
        {data.promo_codes.length === 0 ? (
          <Text color="gray.500">No promo codes created</Text>
        ) : null}
        {data.promo_codes.length > 0 ? (
          <PromoCodesTable
            onEdit={(promoCode) => {
              setModalType(EditModalType.PROMO_CODE);
              setEditingPromoCode(promoCode);
            }}
            readOnly={!hasPermission("write")}
            promoCodes={data.promo_codes}
            loading={isLoading}
          />
        ) : null}
      </Box>
    </Stack>
  );
};

export default PromocodesSection;
